import { useState, useEffect } from "react";
import {
  Typography, Card, Button, Table, Modal, Form, Input, Switch,
  Space, Popconfirm, message, Tag,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  getAllAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement,
} from "../../api/portalService";

const { Title, Text } = Typography;
const { TextArea } = Input;

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    getAllAnnouncements()
      .then(({ data }) => setAnnouncements(data.results || []))
      .catch(() => message.error("Error al cargar anuncios"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true });
    setModalOpen(true);
  };

  const openEdit = (ann) => {
    setEditing(ann);
    form.setFieldsValue({ title: ann.title, content: ann.content, active: ann.active });
    setModalOpen(true);
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await updateAnnouncement(editing.id, values);
        message.success("Anuncio actualizado");
      } else {
        await createAnnouncement(values);
        message.success("Anuncio creado");
      }
      setModalOpen(false);
      load();
    } catch {
      message.error("Error al guardar el anuncio");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteAnnouncement(id);
      message.success("Anuncio eliminado");
      load();
    } catch {
      message.error("Error al eliminar");
    }
  };

  const columns = [
    {
      title: "Título",
      dataIndex: "title",
      key: "title",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Contenido",
      dataIndex: "content",
      key: "content",
      ellipsis: true,
    },
    {
      title: "Estado",
      dataIndex: "active",
      key: "active",
      width: 100,
      render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Activo" : "Inactivo"}</Tag>,
    },
    {
      title: "Creado",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (v) => v ? new Date(v).toLocaleDateString("es-CL") : "-",
    },
    {
      title: "Acciones",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="¿Eliminar anuncio?" onConfirm={() => onDelete(record.id)} okText="Sí" cancelText="No">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: "#1d3557" }}>Anuncios del portal</Title>
          <Text type="secondary">Avisos que verán los clientes en la sección de documentos.</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
          style={{ background: "#1d3557", borderColor: "#1d3557" }}>
          Nuevo anuncio
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }}>
        <Table
          dataSource={announcements}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="middle"
          pagination={false}
        />
      </Card>

      <Modal
        title={editing ? "Editar anuncio" : "Nuevo anuncio"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item label="Título" name="title" rules={[{ required: true, message: "Requerido" }]}>
            <Input placeholder="Ej: Horario de atención" />
          </Form.Item>
          <Form.Item label="Contenido" name="content" rules={[{ required: true, message: "Requerido" }]}>
            <TextArea rows={4} placeholder="Escribe el aviso aquí..." />
          </Form.Item>
          {editing && (
            <Form.Item label="Activo" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="primary" htmlType="submit" loading={saving}>Guardar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Announcements;
